<template>
    <div class="yield-view-page">
        <breadcrumb :items="breadcrumbs"></breadcrumb>
        <v-container fluid class="yield-view-container">
            <!-- Header Section -->
            <div class="report-header">
                <div class="title-row">
                    <h2>{{ $t("reports.yieldPrediction") }}</h2>
                    <v-btn small rounded color="primary" class="guide-button" @click="viewGuide">
                        <v-icon left small>mdi-help-circle-outline</v-icon>
                        {{ $t("reports.aiReportGuide") }}
                    </v-btn>
                </div>
                <v-spacer></v-spacer>
                <div class="header-actions">
                    <v-btn 
                        outlined 
                        color="primary" 
                        class="mr-2" 
                        @click="handleCancel"
                    >
                        {{ $t("reports.back") }}
                    </v-btn>
                    <v-btn 
                        outlined 
                        color="error" 
                        class="mr-2" 
                        @click="handleDeleteReport"
                    >
                        {{ $t("reports.deleteReport") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        dark
                        class="mr-2"
                        :loading="generatingPdf"
                        :disabled="generatingPdf"
                        @click="handleDownload"
                    >
                        <v-icon left>mdi-download</v-icon>
                        {{ $t("reports.download") }}
                    </v-btn>
                </div>
            </div>

            <!-- Report Details Section -->
            <v-card elevation="0" class="report-card report-meta">
                <h3 class="mb-4">{{ reportData.farmName || 'N/A' }} {{ $t("reports.report") }}</h3>
                <v-row>
                    <v-col cols="12" md="3">
                        <div class="mb-2">
                            <span class="font-weight-medium">{{ $t("reports.dateOfReport") }}:</span>
                            <span class="ml-2">{{ formatDate(reportData.dateOfReport) || 'N/A' }}</span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="mb-2">
                            <span class="font-weight-medium">{{ $t("reports.farm") }}:</span>
                            <span class="ml-2">{{ reportData.farmName || 'N/A' }}</span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="mb-2">
                            <span class="font-weight-medium">{{ $t("reports.location") }}:</span>
                            <span class="ml-2">
                                {{ reportData.location || 'N/A' }}
                                <span v-if="reportData.coordinates && reportData.coordinates.lat">
                                    ({{ reportData.coordinates.lat }}, {{ reportData.coordinates.lng }})
                                </span>
                            </span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="mb-2">
                            <span class="font-weight-medium">{{ $t("reports.zone") }}:</span>
                            <span class="ml-2">{{ reportData.zone || 'N/A' }}</span>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3">
                        <div class="mb-2">
                            <span class="font-weight-medium">{{ $t("reports.crop") }}:</span>
                            <span class="ml-2">{{ reportData.crop || 'N/A' }}</span>
                        </div>
                    </v-col>
                </v-row>
            </v-card>

            <!-- Map Section -->
            <v-card elevation="0" class="report-card map-card">
                <div 
                    class="google-map" 
                    id="yield-prediction-view-map" 
                    ref="googleMap"
                >
                    <v-btn
                        icon
                        small
                        class="map-edit-btn"
                        style="position: absolute; bottom: 10px; right: 10px; z-index: 10;"
                        @click="handleEditMap"
                    >
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                </div>
            </v-card>

            <!-- Forecast Section -->
            <v-card elevation="0" class="report-card forecast-card">
                <h3 class="mb-4 green--text">
                    <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                    {{ $t("reports.forecastedYields") }}
                </h3>
                <div class="mb-2">
                    <span class="font-weight-bold">{{ $t("reports.farmArea") }}:</span>
                    <span class="ml-2">{{ reportData.farmArea || '0' }} {{ $t("reports.hectares") }}</span>
                </div>
                <v-row class="mt-4">
                    <!-- Left Panel: Yield Forecasts -->
                    <v-col cols="12" md="4">
                        <v-card elevation="1" class="pa-4">
                            <h4 class="mb-4">{{ $t("reports.yieldForecasts") }}</h4>
                            <div v-if="yieldForecasts && yieldForecasts.length > 0">
                                <div v-for="(forecast, index) in yieldForecasts" :key="index" class="mb-3">
                                    <div class="d-flex justify-space-between align-center">
                                        <span class="font-weight-medium">{{ forecast.year }}:</span>
                                        <span class="font-weight-bold">{{ formatNumber(forecast.yield) }} {{ forecast.unit || 'kg' }}</span>
                                    </div>
                                    <v-divider v-if="index < yieldForecasts.length - 1" class="mt-2"></v-divider>
                                </div>
                            </div>
                            <div v-else class="text-center grey--text">
                                {{ $t("reports.noForecastDataAvailable") }}
                            </div>
                        </v-card>
                    </v-col>

                    <!-- Right Panel: Graph Image -->
                    <v-col cols="12" md="8">
                        <v-card elevation="1" class="pa-4">
                            <div class="d-flex justify-space-between align-center mb-4">
                                <h4>{{ $t("reports.graph") }}</h4>
                                </div>
                            <div v-if="reportData.imageData && reportData.imageData.length > 0" class="graph-image-container">
                                <img 
                                    v-for="(imageUrl, index) in reportData.imageData" 
                                    :key="index"
                                    :src="imageUrl" 
                                    :alt="`Yield prediction graph ${index + 1}`"
                                    class="graph-image"
                                    @error="handleImageError"
                                />
                            </div>
                            <div v-else class="text-center grey--text pa-8">
                                <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-chart-line</v-icon>
                                <p>{{ $t("reports.noGraphImageAvailable") }}</p>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card>

            <!-- Disclaimer Section -->
            <v-alert type="warning" text border="left" icon="mdi-alert" class="report-disclaimer mt-4">
                <strong>{{ $t("reports.yieldPredictionDesign.disclaimerTitle") }}</strong>
                <p class="mt-2 mb-2">{{ $t("reports.yieldPredictionDesign.disclaimerIntro") }}</p>
                <ol class="pl-4 mb-0">
                    <li class="mb-2">{{ $t("reports.yieldPredictionDesign.disclaimer1") }}</li>
                    <li class="mb-2">{{ $t("reports.yieldPredictionDesign.disclaimer2") }}</li>
                    <li class="mb-2">{{ $t("reports.yieldPredictionDesign.disclaimer3") }}</li>
                    <li>{{ $t("reports.yieldPredictionDesign.disclaimer4") }}</li>
                </ol>
            </v-alert>

            <v-divider class="my-4" />

            <feedback-display
                v-if="feedbackReportUid"
                :feedback="feedback"
                i18n-prefix="reports.yieldPredictionDesign"
                @add="showFeedback = true"
            />

            <feedback-dialog
                v-if="feedbackReportUid"
                :value="showFeedback"
                :report-uid="feedbackReportUid"
                :service="feedbackService"
                i18n-prefix="reports.yieldPredictionDesign"
                upload-folder="yield-prediction/feedback"
                @input="showFeedback = $event"
                @submitted="onFeedbackSubmitted"
            />

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="deleteDialog" persistent max-width="500">
                <v-card>
                    <v-toolbar color="error" elevation="0">
                        <v-toolbar-title class="white--text">
                            {{ $t('reports.deleteConfirmation') }}
                        </v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn color="white" icon @click="deleteDialog = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <v-card-text class="pt-5 black--text">
                        <div class="text-center mb-4">
                            <v-icon size="60" color="error">mdi-alert-circle-outline</v-icon>
                        </div>
                        <p class="text-body-1 text-center">
                            {{ $t('reports.confirmDeleteReport') }}
                        </p>
                        <v-divider class="mt-5"></v-divider>
                        <v-card-actions class="pt-4">
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                outlined
                                @click="deleteDialog = false"
                            >
                                {{ $t('cancel') }}
                            </v-btn>
                            <v-btn
                                depressed
                                color="error"
                                @click="confirmDelete"
                                :loading="deleting"
                            >
                                {{ $t('delete') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <yield-prediction-guide-dialog v-model="showGuide" />

        </v-container>
    </div>
</template>

<script>
import html2canvas from 'html2canvas';
import loadingMixin from "@/mixins/LoadingMixin";
import DateMixin from "@/mixins/DateMixin";
import DownloadMixin from "@/mixins/DownloadMixin";
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';
import YieldPredictionService from '@/_services/YieldPredictionService';
import FeedbackDialog from '../shared/FeedbackDialog.vue';
import FeedbackDisplay from '../shared/FeedbackDisplay.vue';
import YieldPredictionGuideDialog from './components/YieldPredictionGuideDialog.vue';

export default {
    components: {
        FeedbackDialog,
        FeedbackDisplay,
        YieldPredictionGuideDialog,
    },
    mixins: [loadingMixin, DateMixin, DownloadMixin],
    data() {
        return {
            reportData: {},
            yieldForecasts: [],
            map: null,
            google: null,
            polygon: null,
            deleteDialog: false,
            deleting: false,
            showGuide: false,
            generatingPdf: false,
            feedback: null,
            showFeedback: false,
            breadcrumbs: [
                {
                    text: this.$t("dashboard") || "Dashboard",
                    disabled: false,
                    to: "/",
                },
                {
                    text: this.$t("reports.yieldPrediction") || "Yield Prediction",
                    disabled: true,
                    to: "",
                },
            ],
        };
    },
    async mounted() {
        await this.fetchReportData();
        await this.loadFeedback();
        // Initialize after data is loaded
        this.$nextTick(async () => {
            await this.initializeMap();
        });
    },
    computed: {
        feedbackService() {
            return YieldPredictionService;
        },
        feedbackReportUid() {
            const id = this.reportData && (this.reportData.id || this.$route.params.id);
            return id != null ? String(id) : '';
        },
    },
    methods: {
        async loadFeedback() {
            if (!this.feedbackReportUid) return;
            try {
                const response = await YieldPredictionService.getYieldPredictionFeedback(
                    this.feedbackReportUid
                );
                if (response && response.success === false) {
                    throw new Error(response.message || response.msg);
                }
                this.feedback = response && response.data ? response.data.feedback : null;
            } catch (error) {
                this.$notify({
                    title: this.$t('reports.unableToLoadReportFeedback') || 'Unable to load feedback',
                    type: 'error',
                });
            }
        },
        onFeedbackSubmitted() {
            this.showFeedback = false;
            this.$notify({
                title: this.$t('success'),
                text: this.$t('reports.yieldPredictionDesign.feedbackSubmitted'),
                type: 'success',
            });
            this.loadFeedback();
        },
        async fetchReportData() {
            const reportId = this.$route.params.id;
            if (reportId) {
                this.startLoading();
                try {
                    const response = await YieldPredictionService.getYieldPredictionDetails(reportId);
                    console.log('API Response:', response);
                    
                    // Service already destructures axios response.data, so response IS the API JSON
                    if (response && response.data && response.data.result) {
                        const data = response.data.result;
                        console.log('Yield Prediction Data:', data);
                        
                        // Parse JSON fields if they are strings
                        const geofenceCoordinates = this.parseJSON(data.geofence_coordinates);
                        const forecastYields = this.parseJSON(data.forecast_yields);
                        const accuracyMatrix = this.parseJSON(data.accuracy_matrix);
                        
                        console.log('Parsed forecast_yields:', forecastYields);
                        console.log('Parsed geofence_coordinates:', geofenceCoordinates);
                        
                        // Set report data
                        this.reportData = {
                            id: data.id,
                            farmId: data.farm_id,
                            farmName: data.farm?.farmName || data.farm_name || 'Unknown Farm',
                            dateOfReport: data.report_date,
                            location: data.location_name || '',
                            coordinates: {
                                lat: parseFloat(data.center_latitude),
                                lng: parseFloat(data.center_longitude)
                            },
                            zone: data.zone_name || '',
                            crop: data.crop_name || '',
                            farmArea: parseFloat(data.area) || 0,
                            geofenceCoordinates: geofenceCoordinates,
                            geofenceType: data.type,
                            countryCode: data.country_code,
                            accuracyMatrix: accuracyMatrix,
                            user: data.user,
                            farm: data.farm,
                            imageData: data.image_data || [],
                        };
                        
                        console.log('Set reportData:', this.reportData);
                        
                        // Set yield forecasts - parse the nested array structure
                        if (forecastYields && Array.isArray(forecastYields)) {
                            const flattened = forecastYields.flat();
                            this.yieldForecasts = flattened
                                .filter(f => f && (f.Date || f.year))
                                .map(f => ({
                                    year: parseInt(f.Date || f.year),
                                    yield: parseFloat(f.Yield || f.yield || 0),
                                    unit: f.unit || 'kg'
                                }))
                                .sort((a, b) => a.year - b.year); // Sort by year
                            console.log('Set yieldForecasts:', this.yieldForecasts);
                        }
                    } else {
                        console.error('Invalid response structure:', response);
                    }
                    
                    this.stopLoading();
                } catch (error) {
                    console.error('Error fetching report data:', error);
                    this.$notify({
                        title: this.$t('error'),
                        text: this.$t('reports.failedToLoadReport'),
                        type: 'error',
                    });
                    this.stopLoading();
                }
            }
        },
        parseJSON(value) {
            if (!value) return null;
            if (typeof value === 'object') return value;
            if (typeof value === 'string') {
                try {
                    return JSON.parse(value);
                } catch (e) {
                    console.error('Error parsing JSON:', e);
                    return null;
                }
            }
            return value;
        },
        handleDeleteReport() {
            this.deleteDialog = true;
        },
        async confirmDelete() {
            const reportId = this.$route.params.id;
            if (!reportId) return;
            
            this.deleting = true;
            try {
                await YieldPredictionService.deleteYieldPrediction(reportId);
                this.deleteDialog = false;
                this.$notify({
                    title: this.$t('success'),
                    text: this.$t('reports.reportDeletedSuccessfully'),
                    type: 'success',
                });
                this.$router.push({ name: 'yieldPredictionList' });
            } catch (error) {
                console.error('Error deleting report:', error);
                this.$notify({
                    title: this.$t('error'),
                    text: this.$t('reports.failedToDeleteReport'),
                    type: 'error',
                });
            } finally {
                this.deleting = false;
            }
        },
        async initializeMap() {
            try {
                const loader = getGoogleMapsLoader();
                const google = await loader.load();
                this.google = google;
                
                const mapContainer = this.$refs.googleMap;
                const center = this.reportData.coordinates && this.reportData.coordinates.lat
                    ? { 
                        lat: parseFloat(this.reportData.coordinates.lat), 
                        lng: parseFloat(this.reportData.coordinates.lng) 
                    }
                    : { lat: 0, lng: 0 };
                
                this.map = new google.maps.Map(mapContainer, {
                    mapTypeId: 'satellite',
                    center: center,
                    zoom: 15,
                    disableDefaultUI: true,
                    zoomControl: true,
                });

                // Draw polygon if geofence coordinates are available
                if (this.reportData.geofenceCoordinates && this.reportData.geofenceCoordinates.length > 0) {
                    // Convert from API format to Google Maps format
                    const coords = this.reportData.geofenceCoordinates.map(coord => ({
                        lat: parseFloat(coord.latitude || coord.lat),
                        lng: parseFloat(coord.longitude || coord.lng)
                    }));
                    this.drawGeofence(coords);
                } else if (center.lat !== 0 && center.lng !== 0) {
                    // Draw a sample rectangle around center if no geofence
                    this.drawSampleGeofence(center);
                }
            } catch (error) {
                console.error('Error initializing map:', error);
            }
        },
        drawGeofence(coordinates) {
            if (!this.google || !this.map || !coordinates || coordinates.length < 3) return;
            
            if (this.polygon) {
                this.polygon.setMap(null);
            }
            
            this.polygon = new this.google.maps.Polygon({
                paths: coordinates,
                strokeColor: "#FF6B00",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#FF6B00",
                fillOpacity: 0.2,
                editable: false,
            });
            
            this.polygon.setMap(this.map);
            
            // Fit bounds to polygon
            const bounds = new this.google.maps.LatLngBounds();
            coordinates.forEach(coord => {
                bounds.extend(new this.google.maps.LatLng(coord.lat, coord.lng));
            });
            this.map.fitBounds(bounds);
        },
        drawSampleGeofence(center) {
            // Draw a sample rectangle around the center
            const offset = 0.005; // ~500m
            
            const coords = [
                { lat: center.lat - offset, lng: center.lng - offset },
                { lat: center.lat - offset, lng: center.lng + offset },
                { lat: center.lat + offset, lng: center.lng + offset },
                { lat: center.lat + offset, lng: center.lng - offset },
            ];
            
            this.drawGeofence(coords);
        },
        handleImageError(event) {
            console.error('Error loading graph image:', event);
            event.target.style.display = 'none';
        },
        formatNumber(num) {
            if (num === null || num === undefined) return '0';
            return num.toLocaleString();
        },
        getProducerName(user) {
            if (!user) return 'N/A';
            const firstName = user.firstName || '';
            const lastName = user.lastName || '';
            const name = `${firstName} ${lastName}`.trim();
            return name || user.email || 'N/A';
        },
        viewGuide() {
            this.showGuide = true;
        },
        handleCancel() {
            this.$router.push({ name: 'yieldPredictionList' });
        },
        async handleDownload() {
            if (!this.reportData || !this.reportData.id) {
                this.$notify({
                    title: this.$t('error'),
                    text: this.$t('reports.reportDataNotLoaded'),
                    type: 'error',
                });
                return;
            }

            this.generatingPdf = true;
            this.startLoading();
            try {
                // The PDF itself is rendered server-side (puppeteer/EJS) — only the
                // interactive map is captured client-side, as a single screenshot,
                // since it can't be re-rendered on the server.
                const formData = new FormData();
                if (this.$refs.googleMap) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    const canvas = await html2canvas(this.$refs.googleMap, { scale: 2, useCORS: true });
                    const imageBlob = this.dataURLtoBlob(canvas.toDataURL('image/jpeg'));
                    formData.append('image', imageBlob, `yield-map-${Date.now()}.jpg`);
                }

                const response = await YieldPredictionService.downloadReport(this.reportData.id, formData);
                this.downloadBlob(response.data, `yield-prediction-report-${this.reportData.id}.pdf`);

                this.$notify({
                    title: this.$t('reports.download'),
                    text: this.$t('reports.pdfGeneratedSuccessfully'),
                    type: 'success',
                });
            } catch (error) {
                console.error('Error downloading PDF:', error);
                this.$notify({
                    title: this.$t('error'),
                    text: this.$t('reports.failedToGeneratePdf'),
                    type: 'error',
                });
            } finally {
                this.generatingPdf = false;
                this.stopLoading();
            }
        },
        dataURLtoBlob(dataUrl) {
            const [header, base64] = dataUrl.split(',');
            const mime = header.match(/:(.*?);/)[1];
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            return new Blob([bytes], { type: mime });
        },
        handleEditMap() {
            // Navigate to edit page or open edit dialog
            this.$notify({
                title: this.$t('reports.editMap'),
                text: this.$t('reports.editMapDescription'),
                type: 'info',
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.yield-view-page {
    min-height: 100%;
    background: #f5f6f8;
    font-size: 14px;
    color: #1f292e;
}

.yield-view-container {
    padding: 20px 28px 40px;
}

.report-header,
.title-row,
.header-actions {
    display: flex;
    align-items: center;
}

.report-header {
    margin-bottom: 18px;
    gap: 16px;
}

.title-row,
.header-actions {
    gap: 12px;
}

.title-row h2 {
    color: #182126;
    font-size: 24px;
}

.guide-button {
    box-shadow: none;
    font-size: 13px;
}

.header-actions .v-btn {
    min-width: 100px;
    height: 40px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 14px;
    font-weight: 600;
}

.report-card {
    margin-bottom: 14px;
    padding: 16px;
    border-radius: 2px;
}

.report-meta {
    h3 {
        color: #263137;
        font-size: 18px;
    }

    ::v-deep .col-12 {
        padding-top: 5px;
        padding-bottom: 5px;
        font-size: 13px;
    }

    ::v-deep .font-weight-medium,
    ::v-deep strong {
        font-size: 14px;
    }
}

.google-map {
    position: relative;
    z-index: 0;
    width: 100%;
    height: 430px;
    border-radius: 2px;
    overflow: hidden;
}

.map-edit-btn {
    background-color: rgba(0, 0, 0, 0.6) !important;
    color: white !important;
}


.graph-image-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 310px;
    background-color: #fff;
    border-radius: 2px;
    padding: 8px;
}

.graph-image {
    max-width: 100%;
    height: auto;
    border-radius: 2px;
    box-shadow: none;
    margin-bottom: 10px;
}

.graph-image:last-child {
    margin-bottom: 0;
}

::v-deep .v-alert {
    background-color: #fff9c4 !important;
}

.forecast-card {
    h3 {
        margin-bottom: 10px !important;
        font-size: 16px;
    }

    ::v-deep .v-card {
        border: 1px solid #e0e4e6;
        border-radius: 2px;
        box-shadow: none !important;
    }
}

.report-disclaimer {
    margin: 14px 0 !important;
    border: 1px solid #ecdca4 !important;
    background: #fffbed !important;
    color: #5d573f;
    font-size: 13px;
    line-height: 1.45;
}

@media (max-width: 900px) {
    .report-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .report-header .v-spacer {
        display: none;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }
}

</style>

